import React, { useMemo } from "react";

declare function __webpack_init_sharing__(key: string): Promise<void>;
declare var __webpack_share_scopes__: Record<string, unknown>;

interface FederatedModule {
  get: (
    module: string
  ) => Promise<() => Promise<{ default: React.ComponentType<unknown> }>>;
  init: (scopes: unknown) => Promise<void>;
}

function loadComponent(scope: string, module: string) {
  return async () => {
    // Initializes the share scope. This fills it with known provided modules from this build and all remotes
    await __webpack_init_sharing__("default");
    const container = (window as unknown as Record<string, FederatedModule>)[
      scope
    ] as FederatedModule;

    // Initialize the container, it may provide shared modules
    await container.init(__webpack_share_scopes__.default);

    const factory = await container.get(module);
    const Module = await factory();
    return Module;
  };
}

const urlCache = new Set();

const useDynamicScript = (url: string) => {
  const [ready, setReady] = React.useState(false);
  const [errorLoading, setErrorLoading] = React.useState(false);

  React.useEffect(() => {
    if (!url) return;

    if (urlCache.has(url)) {
      setReady(true);
      setErrorLoading(false);
      return;
    }

    setReady(false);
    setErrorLoading(false);

    const element = document.createElement("script");

    element.src = `${url}?q=${new Date().valueOf()}`;
    element.type = "text/javascript";
    element.async = true;

    element.onload = () => {
      urlCache.add(url);
      setReady(true);
    };

    element.onerror = () => {
      setReady(false);
      setErrorLoading(true);
    };

    document.head.appendChild(element);

    return () => {
      urlCache.delete(url);
      document.head.removeChild(element);
    };
  }, [url]);

  return {
    errorLoading,
    ready,
  };
};

const componentCache = new Map();

type IFederatedComponentProps<T extends {} = {}> = T & IFederatedComponentInit;

interface IFederatedComponentInit {
  remoteUrl: string;
  scope: string;
  module: string;
}

export const useFederatedComponent = ({
  remoteUrl,
  scope,
  module,
}: IFederatedComponentInit) => {
  const key = `${remoteUrl}-${scope}-${module}`;

  const [Component, setComponent] = React.useState<React.ComponentType<
    Record<string, unknown>
  > | null>();

  const { ready, errorLoading } = useDynamicScript(remoteUrl);

  React.useEffect(() => {
    if (Component) setComponent(null);
    // Only recalculate when key changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  React.useEffect(() => {
    if (ready && !Component) {
      const Comp = React.lazy(loadComponent(scope, module));
      componentCache.set(key, Comp);
      setComponent(Comp);
    }
    // key includes all dependencies (scope/module)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Component, ready, key, scope]);

  return { errorLoading, Component };
};

export function Remote({
  remoteUrl,
  scope,
  module,
  ...props
}: IFederatedComponentProps<Record<string, unknown>>) {
  const { Component: FederatedComponent, errorLoading } = useFederatedComponent(
    { remoteUrl, scope, module }
  );

  const F = useMemo(
    () => FederatedComponent && <FederatedComponent {...props} />,
    [FederatedComponent, props]
  );

  return (
    <React.Suspense fallback="">
      {errorLoading ? `Error loading module` : F}
    </React.Suspense>
  );
}
